import { legalPages } from './legalPages';
import { categoryLabel, collectionCategories, collectionPath, liveDropProducts } from './liveDrop';
import { seoGuidePages, type SeoFaq } from './seoPages';
import { getFaqsForPage, homeFaqs } from './faqContentBank';

export const SITE_URL = 'https://propickleball.nl';

export type RouteKind = 'home' | 'seo_guide' | 'collection' | 'product' | 'legal' | 'tool';

export type BreadcrumbItem = {
  name: string;
  path: string;
};

export type RouteMetadata = {
  path: string;
  title: string;
  description: string;
  kind: RouteKind;
  priority: number;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  breadcrumbs: BreadcrumbItem[];
  faq?: SeoFaq[];
  itemList?: Array<{ name: string; url: string }>;
};

const baseBreadcrumb: BreadcrumbItem = { name: 'ProPickleball.nl', path: '/' };

export function normalizeRoutePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const pathOnly = pathname.split('?')[0].split('#')[0];
  return pathOnly.endsWith('/') ? pathOnly : `${pathOnly}/`;
}

export function absoluteUrl(pathname: string): string {
  const normalized = normalizeRoutePath(pathname);
  return `${SITE_URL}${normalized}`;
}

function withBrand(title: string): string {
  return title.includes('ProPickleball.nl') ? title : `${title} | ProPickleball.nl`;
}

export function getRouteMetadata(): RouteMetadata[] {
  const home: RouteMetadata = {
    path: '/',
    title: 'ProPickleball.nl | Pickleball kopen met keuzehulp voor NL en BE',
    description: 'Prelaunch pickleball specialist voor Nederland en Belgie. Bekijk startersets, paddles, ballen, netten en clubpakketten zonder fake stock of checkoutclaims.',
    kind: 'home',
    priority: 1,
    changefreq: 'weekly',
    breadcrumbs: [baseBreadcrumb]
  };

  const toolRoutes: RouteMetadata[] = [
    {
      path: '/producten/',
      title: 'Pickleball producten bekijken | ProPickleball.nl',
      description: 'Bekijk de prelaunch collectie van ProPickleball.nl met prijsalerts, richtprijzen en beschikbaarheidsupdates voor pickleball producten.',
      kind: 'tool',
      priority: 0.85,
      changefreq: 'weekly',
      breadcrumbs: [baseBreadcrumb, { name: 'Producten', path: '/producten/' }]
    },
    {
      path: '/keuzehulp/',
      title: 'Pickleball keuzehulp | ProPickleball.nl',
      description: 'Kies tussen startersets, paddles, ballen, netten en clubpakketten op basis van speler, speelplek en niveau.',
      kind: 'tool',
      priority: 0.8,
      changefreq: 'monthly',
      breadcrumbs: [baseBreadcrumb, { name: 'Keuzehulp', path: '/keuzehulp/' }]
    }
  ];

  const legalRoutes: RouteMetadata[] = legalPages.map((page) => ({
    path: page.slug,
    title: withBrand(page.title),
    description: page.summary,
    kind: 'legal',
    priority: page.slug === '/contact/' ? 0.65 : 0.35,
    changefreq: 'monthly',
    breadcrumbs: [baseBreadcrumb, { name: page.title, path: page.slug }]
  }));

  const guideRoutes: RouteMetadata[] = seoGuidePages.map((page) => ({
    path: page.slug,
    title: withBrand(page.title),
    description: page.meta,
    kind: 'seo_guide',
    priority: page.cluster.startsWith('geo') ? 0.72 : 0.82,
    changefreq: 'monthly',
    breadcrumbs: [baseBreadcrumb, { name: 'Keuzehulpen', path: '/#gidsen' }, { name: page.title, path: page.slug }],
    faq: getFaqsForPage(page)
  }));

  const collectionRoutes: RouteMetadata[] = collectionCategories.map((category) => {
    const products = liveDropProducts.filter((product) => product.category === category.key);
    return {
      path: collectionPath(category.key),
      title: `${categoryLabel(category.key)} bekijken | ProPickleball.nl`,
      description: `${categoryLabel(category.key)} in de prelaunch collectie met prijsalert, richtprijzen en beschikbaarheidsstatus. Geen checkout of voorraadclaim zonder bewijs.`,
      kind: 'collection',
      priority: 0.74,
      changefreq: 'weekly',
      breadcrumbs: [baseBreadcrumb, { name: 'Producten', path: '/producten/' }, { name: categoryLabel(category.key), path: collectionPath(category.key) }],
      itemList: products.map((product) => ({ name: product.name, url: product.pdpPath }))
    };
  });

  const productRoutes: RouteMetadata[] = liveDropProducts.map((product) => ({
    path: product.pdpPath,
    title: withBrand(product.name),
    description: `${product.name}: prelaunch productpagina met prijsalert, richtprijs en beschikbaarheidsstatus. Geen checkout, voorraadclaim of GTIN-publicatie zonder bewijs.`,
    kind: 'product',
    priority: 0.62,
    changefreq: 'weekly',
    breadcrumbs: [
      baseBreadcrumb,
      { name: 'Producten', path: '/producten/' },
      { name: categoryLabel(product.category), path: collectionPath(product.category) },
      { name: product.name, path: product.pdpPath }
    ]
  }));

  return [home, ...toolRoutes, ...guideRoutes, ...collectionRoutes, ...productRoutes, ...legalRoutes];
}

export function findRouteMetadata(pathname: string): RouteMetadata | null {
  const normalized = normalizeRoutePath(pathname);
  return getRouteMetadata().find((route) => route.path === normalized) ?? null;
}

function breadcrumbSchema(route: RouteMetadata) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${absoluteUrl(route.path)}#breadcrumb`,
    itemListElement: route.breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  };
}

export function buildRouteSchema(route: RouteMetadata): unknown[] {
  if (route.kind === 'home') {
    return [
      {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            name: 'ProPickleball.nl',
            url: `${SITE_URL}/`,
            logo: `${SITE_URL}/brand/propickleball-logo-512.png`,
            description: route.description,
            areaServed: ['NL', 'BE'],
            parentOrganization: {
              '@type': 'Organization',
              name: 'Sevensa / Klokkers Consultancy'
            }
          },
          {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            url: `${SITE_URL}/`,
            name: 'ProPickleball.nl',
            publisher: { '@id': `${SITE_URL}/#organization` },
            inLanguage: 'nl-NL'
          },
          {
            '@type': 'FAQPage',
            '@id': `${SITE_URL}/#faq`,
            inLanguage: 'nl-NL',
            mainEntity: homeFaqs.map((item) => ({
              '@type': 'Question',
              '@id': `${SITE_URL}/#faq-${item.id}`,
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer }
            }))
          }
        ]
      }
    ];
  }

  const pageType = route.kind === 'collection' ? 'CollectionPage' : 'WebPage';
  const schemas: unknown[] = [
    {
      '@context': 'https://schema.org',
      '@type': pageType,
      '@id': `${absoluteUrl(route.path)}#webpage`,
      url: absoluteUrl(route.path),
      name: route.title,
      description: route.description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${absoluteUrl(route.path)}#breadcrumb` },
      inLanguage: 'nl-NL'
    },
    breadcrumbSchema(route)
  ];

  if (route.kind === 'seo_guide' && route.faq?.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${absoluteUrl(route.path)}#faq`,
      mainEntity: route.faq.map((item) => ({
        '@type': 'Question',
        ...(item.id ? { '@id': `${absoluteUrl(route.path)}#faq-${item.id}` } : {}),
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    });
  }

  if (route.kind === 'collection' && route.itemList?.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${absoluteUrl(route.path)}#itemlist`,
      itemListElement: route.itemList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: absoluteUrl(item.url)
      }))
    });
  }

  return schemas;
}
